import { Daycare, DaycareType } from '@/types/Daycare.ts'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import { OccasionalSlotsPlaces } from '@/types/Slots.ts'
import { Section } from '@/types/Section.ts'
import { StaffLevel } from '@/types/StaffLevel.ts'
import { CalendarEventType } from '@/types/Calendar.ts'
import { Child, ChildDaycare } from '@/types/Child.ts'
import { DateRateSlot } from '@/types/StaffRate.ts'
import { PlanningTabId } from '@/hooks/usePlanningRights.ts'

const typeOccasionalSlots: CalendarEventType[] = [
    CalendarEventType.AVAILABLE,
    CalendarEventType.SCHEDULED,
    CalendarEventType.REQUEST,
    CalendarEventType.VALIDATED
]

const typeWorkSlots: CalendarEventType[] = [
    CalendarEventType.IN_SECTION,
    CalendarEventType.STAFF_TRAINING,
    CalendarEventType.STAFF_HOLIDAY,
    CalendarEventType.NOT_IN_SECTION
]

const typeRegularSlots: CalendarEventType[] = [
    CalendarEventType.REGULAR,
    CalendarEventType.ABSENCE,
    CalendarEventType.REGULAR_REQUEST
]

const typeParentSlots: CalendarEventType[] = [
    CalendarEventType.AVAILABLE,
    CalendarEventType.REQUEST,
    CalendarEventType.VALIDATED,
    CalendarEventType.REGULAR,
    CalendarEventType.ABSENCE,
    CalendarEventType.REGULAR_REQUEST
]

export function getSelectOptionsFilters(
    t: (s: string) => string,
    planningActiveTab: PlanningTabId,
    daycare: Daycare,
    places: OccasionalSlotsPlaces[],
    rates: DateRateSlot[],
    canReadStaffWorks: boolean,
    childs?: Child[]
): SelectOptionsDeprecated {
    const result: SelectOptionsDeprecated = []

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    switch (planningActiveTab) {
        case PlanningTabId.PARENT_TAB:
            result.push({ name: t('slots'), options: getSlotsOptions(t, typeParentSlots) })
            if (childs && childs.length > 0) {
                result.push({ name: t('childs'), options: getChildsOptions(childs, daycare.id) })
            }
            if (places.length > 0) result.push({ name: t('places'), options: getPlacesOptions(places) })
            break
        case PlanningTabId.REGULAR_TAB:
            if (daycare.sections && daycare.sections.length > 0) {
                result.push({
                    name: t(isRpeOrLaep ? 'sectors' : 'sections'),
                    options: getSectionsOptions(daycare.sections)
                })
            }
            result.push({ name: t('contracts'), options: getSlotsOptions(t, typeRegularSlots) })
            if (places.length > 0) result.push({ name: t('places'), options: getPlacesOptions(places) })
            break
        case PlanningTabId.OCCASIONAL_TAB:
            if (daycare.sections && daycare.sections.length > 0) {
                result.push({
                    name: t(isRpeOrLaep ? 'sectors' : 'sections'),
                    options: getSectionsOptions(daycare.sections)
                })
            }
            result.push({ name: t('slots'), options: getSlotsOptions(t, typeOccasionalSlots) })
            if (places.length > 0) result.push({ name: t('places'), options: getPlacesOptions(places) })

            break
        case PlanningTabId.STAFF_TAB:
            if (canReadStaffWorks && daycare.levels && daycare.levels.length > 0) {
                result.push({ name: t('levels'), options: getLevelsOptions(daycare.levels) })
            }
            result.push({ name: t('type'), options: getWorkOptions(t, typeWorkSlots) })
            break
        case PlanningTabId.GLOBAL_TAB:
            if (rates.length > 0) {
                result.push({ name: t('staffRate'), options: getRatesOptions(rates) })
            }
            break
        default:
            break
    }

    return result
}

function getSlotsOptions(t: (s: string) => string, types: CalendarEventType[]): SelectOptionDeprecated[] {
    const result: SelectOptionDeprecated[] = []
    types.forEach((type: CalendarEventType) => {
        result.push({ name: getCalendarEventTypeName(t, type), value: type })
    })
    return result
}

function getCalendarEventTypeName(t: (s: string) => string, type: CalendarEventType): string {
    switch (type) {
        case CalendarEventType.AVAILABLE:
            return t('available')
        case CalendarEventType.SCHEDULED:
            return t('schedule')
        case CalendarEventType.REQUEST:
            return t('requestSlot')
        case CalendarEventType.VALIDATED:
            return t('validated')
        case CalendarEventType.REGULAR:
            return t('regular')
        case CalendarEventType.ABSENCE:
            return t('absence')
        case CalendarEventType.REGULAR_REQUEST:
            return t('regularRequestWaiting')
        case CalendarEventType.STAFF_TRAINING:
            return t('training')
        case CalendarEventType.IN_SECTION:
            return t('inSection')
        case CalendarEventType.STAFF_HOLIDAY:
            return t('holiday')
        case CalendarEventType.NOT_IN_SECTION:
            return t('notInSection')
        default:
            return ''
    }
}

function getWorkOptions(t: (s: string) => string, types: CalendarEventType[]): SelectOptionDeprecated[] {
    const result: SelectOptionDeprecated[] = []
    types.forEach((type: CalendarEventType) => {
        result.push({ name: getCalendarEventTypeName(t, type), value: type })
    })
    return result
}

function getChildsOptions(childs: Child[], daycareId: number): SelectOptionDeprecated[] {
    const result: SelectOptionDeprecated[] = []
    childs
        .filter((child: Child) =>
            child.daycares.find((daycare: ChildDaycare) => daycare.daycareId === daycareId && daycare.visible)
        )
        .forEach((child: Child) => {
            result.push({ name: child.firstname, value: 'CHILD_' + child.id.toString() })
        })
    return result
}

function getSectionsOptions(sections: Section[]): SelectOptionDeprecated[] {
    const result: SelectOptionDeprecated[] = []
    sections.forEach((section: Section) => {
        result.push({ name: section.name, value: 'SECTION_' + section.id.toString() })
    })
    return result
}

function getPlacesOptions(places: OccasionalSlotsPlaces[]): SelectOptionDeprecated[] {
    const result: SelectOptionDeprecated[] = []
    places.forEach((place: OccasionalSlotsPlaces) => {
        result.push({ name: place.name, value: 'PLACE_' + place.id.toString() })
    })
    return result
}

function getLevelsOptions(levels: StaffLevel[]): SelectOptionDeprecated[] {
    const result: SelectOptionDeprecated[] = []
    levels.forEach((level: StaffLevel) => {
        result.push({ name: level.name, value: 'LEVEL_' + level.id.toString() })
    })
    return result
}

function getRatesOptions(rates: DateRateSlot[]): SelectOptionDeprecated[] {
    const result: SelectOptionDeprecated[] = []
    rates.forEach((rate: DateRateSlot) => {
        if (
            !result.find((option: SelectOptionDeprecated) => option.value === 'RATE_' + rate.sectionGroupId.toString())
        ) {
            result.push({ name: rate.sectionGroupName, value: 'RATE_' + rate.sectionGroupId.toString() })
        }
    })
    return result
}
